import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DAY_KEY_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}
const NUMBER_TO_DAY_KEY: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
}
const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const
type DayKey = typeof DAY_KEYS[number]

interface ParsedStore {
  store_number: string
  delivery_days: string[]
  location_id: number
  location_name: string
}

interface Combination {
  delivery_days: number[]
  delivery_items: string[]
  schedule_data: Array<{
    prep_item_id: string
    prep_item_name: string
    is_safe: boolean
    [key: string]: any
  }>
  source: 'file' | 'manual'
  store_count: number
  warnings?: Array<{ rule: string; reason: string; prep_item_id?: string }>
}

type Warning = NonNullable<Combination['warnings']>[number]

// Translate algorithm-internal warnings into something an admin can act on.
// The four `rule` codes come from the backend derivation algorithm:
//   cycle_gap       — long prep-free stretch (the only one the admin can fix
//                     by editing the grid; recomputes live as cells toggle).
//   rule2_adjacent  — prep day forced next to a delivery day (placement
//                     diagnostic; clears once the admin edits the combo).
//   rule3_adjacent  — no ideal "day before delivery" slot (ditto).
//   rule4_gap       — gap couldn't be fully closed (ditto).
const friendlyWarning = (
  w: Warning,
  itemName?: string,
): { title: string; detail: string } => {
  const item = itemName || w.prep_item_id || 'this item'
  switch (w.rule) {
    case 'cycle_gap': {
      const gap = w.reason.match(/is (\d+) days/)?.[1] || '5+'
      const span = w.reason.match(/\(([^)]+)\)/)?.[1] || ''
      return {
        title: `Long gap for ${item}: sits ${gap} days between preps${
          span ? ` (${span})` : ''
        }`,
        detail:
          'Add a prep day on a non-delivery day to shrink the gap, or mark the item as a delivery-safe item if it keeps well.',
      }
    }
    case 'rule2_adjacent':
      return {
        title: `Prep day landed next to a delivery day for ${item}`,
        detail:
          "Not enough non-delivery days to avoid an adjacent slot. The algorithm picked the best available; edit the grid if you'd prefer a different day.",
      }
    case 'rule3_adjacent':
      return {
        title: `No ideal "day before delivery" slot for ${item}`,
        detail:
          'The algorithm placed the prep day as far as possible from existing prep days. Edit the grid to override.',
      }
    case 'rule4_gap':
      return {
        title: `Couldn't fully close the prep-cycle gap for ${item}`,
        detail:
          'Not enough non-delivery days — the gap was kept or reduced as much as possible.',
      }
    default:
      return { title: 'Schedule constraint warning', detail: w.reason }
  }
}

// sync: kit-core/server/src/routes/customers/fw_wingstop/deliverySchedule/algorithm/applyCombinationRules.ts
// Mirrors `emitCycleGapWarnings` so admin edits can recompute warnings live
// without a backend round-trip. Threshold (>4) and day numbering (0=Sun…6=Sat)
// must stay aligned with the BE.
const computeCycleGapWarnings = (
  scheduleData: Combination['schedule_data'],
): Warning[] => {
  const warnings: Warning[] = []
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const dayKeysByNum = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]
  for (const item of scheduleData) {
    if (item.is_safe) continue
    const days: number[] = []
    for (let i = 0; i < 7; i += 1) if (item[dayKeysByNum[i]]) days.push(i)
    if (days.length === 0) continue
    let maxGap = 0
    let from = days[0]
    let to = days[0]
    for (let i = 0; i < days.length; i += 1) {
      const a = days[i]
      const b = days[(i + 1) % days.length]
      const gap = i + 1 < days.length ? b - a : 7 - a + b
      if (gap > maxGap) {
        maxGap = gap
        from = a
        to = b
      }
    }
    if (maxGap > 4) {
      warnings.push({
        prep_item_id: item.prep_item_id,
        rule: 'cycle_gap',
        reason: `Max prep-free stretch is ${maxGap} days (${dayNames[from]} → ${dayNames[to]}). May risk shelf life.`,
      })
    }
  }
  return warnings
}

interface StoreAssignment {
  location_id: number
  store_number: string
  location_name: string
  combination_index: number | null
  effective_date: string
  source: 'file' | 'manual'
}

const STEPS = [
  'Upload & Parse',
  'Prep Base Schedule',
  'Delivery Combinations',
  'Store Assignment',
  'Dates & Commit',
]

interface ActiveSchedule {
  scheduleId: number
  scheduleName: string
  scheduleType: string
  startDate: string
  endDate: string | null
  status: string
  storeCount: number
}

interface Props {
  allPrepItems: Array<{ id: string; name: string }>
  globalSafeItems: Set<string>
  allLocations: Array<{ id: number; name: string; code: string }>
  activeSchedules: ActiveSchedule[]
  onCommitted: () => void
}

const ImportWizard: React.FC<Props> = ({
  allPrepItems,
  globalSafeItems,
  allLocations,
  activeSchedules,
  onCommitted,
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [uploading, setUploading] = useState(false)
  // why: on staging the parse + derivation round-trip can take ~8s
  // (parse ~5s, preview_combos ~3s). A bare spinner doesn't tell the user
  // anything is happening beyond "uploading", so we surface the current
  // stage so a slow compute doesn't look like a hang.
  const [uploadingStage, setUploadingStage] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [commitProgress, setCommitProgress] = useState<{
    current: number
    total: number
  } | null>(null)

  // Add Combination day picker
  const [addingCombo, setAddingCombo] = useState(false)
  const [newComboDays, setNewComboDays] = useState<Set<number>>(new Set())

  // Step 1 data
  const [parsedStores, setParsedStores] = useState<ParsedStore[]>([])
  const [filename, setFilename] = useState('')

  // Step 2 data
  const [combinations, setCombinations] = useState<Combination[]>([])
  const [editingComboIdx, setEditingComboIdx] = useState<number | null>(null)
  // why: track which combos the admin has edited. Placement warnings
  // (rule2/3/4_*) reflect the auto-fill's reasoning — once the admin
  // overrides any cell those notes are stale, so we drop them and keep
  // only the live-recomputed cycle_gap check.
  const [touchedCombos, setTouchedCombos] = useState<Set<number>>(new Set())

  // Step 3 data
  const [storeAssignments, setStoreAssignments] = useState<StoreAssignment[]>(
    [],
  )

  // Step 1 data — per-schedule prep base.
  // why: admins can keep the global Template (default) or customize this
  // schedule's base BEFORE Step 2 computes combinations. The chosen grid
  // becomes the algorithm's input and is persisted as a per-schedule base
  // at commit time (rows in prep_item_weekday_config_2 with
  // delivery_schedule_id = new schedule's id).
  const [baseMode, setBaseMode] = useState<'template' | 'custom'>('template')
  const [templateGrid, setTemplateGrid] = useState<
    Array<{
      prep_item_id: string
      prep_item_name: string
      sunday: boolean
      monday: boolean
      tuesday: boolean
      wednesday: boolean
      thursday: boolean
      friday: boolean
      saturday: boolean
    }>
  >([])
  const [customBaseGrid, setCustomBaseGrid] = useState<
    Array<{
      prep_item_id: string
      prep_item_name: string
      sunday: boolean
      monday: boolean
      tuesday: boolean
      wednesday: boolean
      thursday: boolean
      friday: boolean
      saturday: boolean
    }>
  >([])

  // Step 5 data
  const [scheduleName, setScheduleName] = useState('')
  // why: Step 4 lets admins either CREATE a brand-new schedule (default) or
  // ADD the upload to an existing schedule. In append mode we reuse the
  // target schedule's metadata (name/type/dates) and detect store conflicts
  // (uploaded store already on the schedule with different days).
  const [commitMode, setCommitMode] = useState<'create' | 'append'>('create')
  const [appendToScheduleId, setAppendToScheduleId] = useState<number | null>(
    null,
  )
  const [targetScheduleStores, setTargetScheduleStores] = useState<
    Array<{
      location_id: number
      store_number: string
      location_name: string
      delivery_days: string[]
    }>
  >([])
  const [loadingTarget, setLoadingTarget] = useState(false)
  // Per-conflict choice: 'use_file' (override existing with file's days,
  // the default per spec) or 'keep_existing' (drop the store from commit).
  const [storeResolutions, setStoreResolutions] = useState<
    Map<number, 'use_file' | 'keep_existing'>
  >(new Map())
  const [scheduleType, setScheduleType] = useState<'regular' | 'event'>(
    'regular',
  )
  const [defaultDate, setDefaultDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [eventEndDate, setEventEndDate] = useState('')
  const [pastDateConfirmed, setPastDateConfirmed] = useState(false)
  const [committing, setCommitting] = useState(false)

  // Fetch the Template base grid when reaching Step 1, so the Customize
  // editor has the current Template values pre-loaded.
  useEffect(() => {
    if (activeStep !== 1 || templateGrid.length > 0) return
    let cancelled = false
    axios
      .get(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/template_base_grid`,
      )
      .then((resp) => {
        if (cancelled) return
        const grid = (resp.data?.grid || []).map((r: any) => ({
          prep_item_id: r.prep_item_id,
          prep_item_name: r.prep_item_name,
          sunday: !!r.sunday,
          monday: !!r.monday,
          tuesday: !!r.tuesday,
          wednesday: !!r.wednesday,
          thursday: !!r.thursday,
          friday: !!r.friday,
          saturday: !!r.saturday,
        }))
        setTemplateGrid(grid)
        // Seed the custom grid with the Template so admin starts from there.
        setCustomBaseGrid(grid.map((r: any) => ({ ...r })))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [activeStep, templateGrid.length])

  // Navigation guard — warn before leaving with unsaved progress
  const hasProgress = parsedStores.length > 0
  useEffect(() => {
    if (!hasProgress) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasProgress])

  // --- Step 1: Upload & Parse ---

  // why: map a backend-computed grid (prep_item_id + day booleans, no name) onto
  // the FE row shape, filling prep_item_name + is_safe from the local catalog.
  // Days default to false when the backend returned no row (e.g. preview failed),
  // in which case wizard_commit fills the grid on save as a backstop.
  const toFeGrid = useCallback(
    (beScheduleData?: Array<Record<string, any>>) =>
      allPrepItems.map((item) => {
        const beRow = (beScheduleData || []).find(
          (r) => r.prep_item_id === item.id,
        )
        return {
          prep_item_id: item.id,
          prep_item_name: item.name,
          is_safe: globalSafeItems.has(item.id),
          sunday: !!beRow?.sunday,
          monday: !!beRow?.monday,
          tuesday: !!beRow?.tuesday,
          wednesday: !!beRow?.wednesday,
          thursday: !!beRow?.thursday,
          friday: !!beRow?.friday,
          saturday: !!beRow?.saturday,
        }
      }),
    [allPrepItems, globalSafeItems],
  )

  // why: ask the backend to apply the 4-rule (or Simple) derivation per delivery-day
  // pattern so the wizard pre-fills each combination. Returns a map keyed by the
  // sorted day list. Failures resolve to an empty map — combos then start blank and
  // wizard_commit fills them on save.
  const fetchPreviewCombos = useCallback(
    async (patterns: number[][], customBase?: Array<Record<string, any>>) => {
      const map = new Map<
        string,
        { schedule_data: Array<Record<string, any>>; warnings: any[] }
      >()
      if (patterns.length === 0) return map
      try {
        const body: Record<string, any> = { patterns }
        // why: when admin customized the per-schedule base in Step 1, pass
        // it so the algorithm runs against that grid instead of the global
        // Template.
        if (customBase && customBase.length > 0) {
          body.base_grid = customBase.map((r) => ({
            prep_item_id: r.prep_item_id,
            prep_item_name: r.prep_item_name,
            sunday: !!r.sunday,
            monday: !!r.monday,
            tuesday: !!r.tuesday,
            wednesday: !!r.wednesday,
            thursday: !!r.thursday,
            friday: !!r.friday,
            saturday: !!r.saturday,
          }))
        }
        const resp = await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/preview_combos`,
          body,
        )
        for (const c of resp.data?.combos || []) {
          const key = [...c.delivery_days]
            .sort((a: number, b: number) => a - b)
            .join(',')
          map.set(key, {
            schedule_data: c.schedule_data,
            warnings: c.warnings || [],
          })
        }
      } catch {
        // leave map empty — caller falls back to blank grids
      }
      return map
    },
    [],
  )

  const processFile = useCallback(
    async (file: File) => {
      setUploading(true)
      setUploadingStage('Uploading and parsing file…')
      setError(null)
      setFilename(file.name)

      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/parse`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        const data = response.data
        setParsedStores(data.matched || [])

        // Auto-generate combinations from parsed stores
        const DAY_NAME_TO_NUM: Record<string, number> = {
          Monday: 1,
          Tuesday: 2,
          Wednesday: 3,
          Thursday: 4,
          Friday: 5,
          Saturday: 6,
          Sunday: 0,
        }

        const patternMap = new Map<string, { days: number[]; count: number }>()
        for (const store of data.matched || []) {
          const dayNums = (store.delivery_days || [])
            .map((d: string) => DAY_NAME_TO_NUM[d])
            .filter((n: number | undefined) => n !== undefined)
            .sort((a: number, b: number) => a - b)
          const key = dayNums.join(',')
          if (!patternMap.has(key)) {
            patternMap.set(key, { days: dayNums, count: 0 })
          }
          patternMap.get(key)!.count++
        }

        // Pre-fill each combination's grid from the backend derivation
        // (4-rule / Simple per the global setting) so the admin reviews
        // exceptions instead of configuring every cell by hand.
        setUploadingStage('Computing combination schedules…')
        const previewByKey = await fetchPreviewCombos(
          Array.from(patternMap.values()).map((p) => p.days),
        )
        const combos: Combination[] = Array.from(patternMap.values()).map(
          (p) => {
            const preview = previewByKey.get(p.days.join(','))
            return {
              delivery_days: p.days,
              delivery_items: Array.from(globalSafeItems),
              schedule_data: toFeGrid(preview?.schedule_data),
              warnings: preview?.warnings || [],
              source: 'file' as const,
              store_count: p.count,
            }
          },
        )
        setCombinations(combos)

        // Build store assignments
        const assignments: StoreAssignment[] = (data.matched || []).map(
          (store: ParsedStore) => {
            const dayNums = (store.delivery_days || [])
              .map((d: string) => DAY_NAME_TO_NUM[d])
              .filter((n: number | undefined) => n !== undefined)
              .sort((a: number, b: number) => a - b)
            const key = dayNums.join(',')
            const comboIdx = combos.findIndex(
              (c) => c.delivery_days.join(',') === key,
            )
            return {
              location_id: store.location_id,
              store_number: store.store_number,
              location_name: store.location_name,
              combination_index: comboIdx >= 0 ? comboIdx : null,
              effective_date: '',
              source: 'file' as const,
            }
          },
        )
        setStoreAssignments(assignments)
      } catch (err: any) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.details?.[0] ||
            'Upload failed',
        )
      } finally {
        setUploading(false)
        setUploadingStage('')
      }
    },
    [globalSafeItems, fetchPreviewCombos, toFeGrid],
  )

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) processFile(file)
      event.target.value = ''
    },
    [processFile],
  )

  // --- Step 2: Toggle safe item ---
  const toggleSafeItem = useCallback((comboIdx: number, itemId: string) => {
    setTouchedCombos((s) => {
      if (s.has(comboIdx)) return s
      const next = new Set(s)
      next.add(comboIdx)
      return next
    })
    setCombinations((prev) =>
      prev.map((c, i) => {
        if (i !== comboIdx) return c
        const items = new Set(c.delivery_items)
        if (items.has(itemId)) items.delete(itemId)
        else items.add(itemId)
        const newItems = Array.from(items)
        const grid = c.schedule_data.map((g) => {
          const isSafe = newItems.includes(g.prep_item_id)
          if (g.prep_item_id !== itemId) return { ...g, is_safe: isSafe }
          const updated: any = { ...g, is_safe: isSafe }
          // When becoming non-safe, force delivery day columns OFF
          if (!isSafe) {
            for (const dayNum of c.delivery_days) {
              const dayKey = NUMBER_TO_DAY_KEY[dayNum]
              if (dayKey) updated[dayKey] = false
            }
          }
          return updated
        })
        return { ...c, delivery_items: newItems, schedule_data: grid }
      }),
    )
  }, [])

  const toggleGridDay = useCallback(
    (comboIdx: number, prepItemId: string, day: DayKey) => {
      setTouchedCombos((s) => {
        if (s.has(comboIdx)) return s
        const next = new Set(s)
        next.add(comboIdx)
        return next
      })
      setCombinations((prev) =>
        prev.map((c, i) => {
          if (i !== comboIdx) return c
          return {
            ...c,
            schedule_data: c.schedule_data.map((g) => {
              if (g.prep_item_id !== prepItemId) return g
              const dayNum = DAY_KEY_TO_NUMBER[day]
              // Non-safe items can't be checked on delivery days
              if (!g.is_safe && c.delivery_days.includes(dayNum)) return g
              return { ...g, [day]: !g[day] }
            }),
          }
        }),
      )
    },
    [],
  )

  // why: live cycle_gap (recomputed from the current grid) merged with the
  // initial BE placement warnings — but only while the combo is untouched.
  // Once edited, the BE rule2/3/4 notes describe a placement the admin has
  // overridden, so they're dropped.
  const getActiveWarnings = useCallback(
    (combo: Combination, idx: number): Warning[] => {
      const live = computeCycleGapWarnings(combo.schedule_data)
      if (touchedCombos.has(idx)) return live
      const placement = (combo.warnings || []).filter(
        (w) => w.rule !== 'cycle_gap',
      )
      return [...placement, ...live]
    },
    [touchedCombos],
  )

  // --- Step 4: Commit ---
  // why: when the admin picks an existing schedule to append to, fetch its
  // active stores so we can flag conflicts (uploaded store already on the
  // schedule with different days). Default each conflict to "use_file" per
  // spec — admin can flip to "keep_existing" per-row.
  useEffect(() => {
    if (commitMode !== 'append' || appendToScheduleId === null) {
      setTargetScheduleStores([])
      setStoreResolutions(new Map())
      return
    }
    let cancelled = false
    setLoadingTarget(true)
    axios
      .get(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/${appendToScheduleId}/details`,
      )
      .then((resp) => {
        if (cancelled) return
        const stores = resp.data?.stores || []
        setTargetScheduleStores(stores)
        // seed resolutions: every overlapping store starts as 'use_file'
        const newResolutions = new Map<number, 'use_file' | 'keep_existing'>()
        const targetIds = new Set<number>(stores.map((s: any) => s.location_id))
        for (const a of storeAssignments) {
          if (targetIds.has(a.location_id)) {
            newResolutions.set(a.location_id, 'use_file')
          }
        }
        setStoreResolutions(newResolutions)
      })
      .catch(() => {
        if (!cancelled) {
          setTargetScheduleStores([])
          setStoreResolutions(new Map())
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTarget(false)
      })
    return () => {
      cancelled = true
    }
  }, [commitMode, appendToScheduleId, storeAssignments])

  // Stores in the upload that are already on the target schedule (any days).
  // Surfaces a conflict row in the wizard so the admin can decide per-store.
  const storeConflicts = useMemo(() => {
    if (commitMode !== 'append' || targetScheduleStores.length === 0) return []
    const targetByLoc = new Map(
      targetScheduleStores.map((s) => [s.location_id, s]),
    )
    return storeAssignments
      .filter((a) => targetByLoc.has(a.location_id))
      .map((a) => {
        const existing = targetByLoc.get(a.location_id)!
        const combo =
          a.combination_index !== null
            ? combinations[a.combination_index]
            : null
        const fileDays = combo
          ? combo.delivery_days
              .map((d) => NUMBER_TO_DAY_KEY[d])
              .filter(Boolean)
              .map((k) => k!.charAt(0).toUpperCase() + k!.slice(1))
          : []
        return {
          location_id: a.location_id,
          store_number: a.store_number,
          location_name: a.location_name,
          existing_days: existing.delivery_days,
          file_days: fileDays,
        }
      })
  }, [commitMode, targetScheduleStores, storeAssignments, combinations])

  const appendTarget = useMemo(
    () =>
      activeSchedules.find((s) => s.scheduleId === appendToScheduleId) ?? null,
    [activeSchedules, appendToScheduleId],
  )

  const assignedStores = useMemo(
    () => storeAssignments.filter((s) => s.combination_index !== null),
    [storeAssignments],
  )

  const handleCommit = useCallback(async () => {
    setCommitting(true)
    setCommitProgress({ current: 0, total: assignedStores.length })
    setError(null)
    try {
      // why: when appending, drop stores the admin chose to "keep existing".
      // The BE doesn't know about resolutions — it just inserts what we send.
      const isAppend = commitMode === 'append' && appendToScheduleId !== null
      const storesToSend = isAppend
        ? assignedStores.filter(
            (s) => storeResolutions.get(s.location_id) !== 'keep_existing',
          )
        : assignedStores
      // why: when admin customized the per-schedule base in Step 1, send
      // it so wizard_commit persists per-schedule prep_item_weekday_config_2
      // rows at (location_id=0, delivery_schedule_id=NEW_ID).
      const customBasePayload =
        !isAppend && baseMode === 'custom' && customBaseGrid.length > 0
          ? customBaseGrid.map((r) => ({
              prep_item_id: r.prep_item_id,
              sunday: r.sunday,
              monday: r.monday,
              tuesday: r.tuesday,
              wednesday: r.wednesday,
              thursday: r.thursday,
              friday: r.friday,
              saturday: r.saturday,
            }))
          : null
      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/wizard_commit`,
        {
          ...(isAppend
            ? { append_to_schedule_id: appendToScheduleId }
            : {
                schedule_name:
                  scheduleName || `Schedule ${new Date().toLocaleDateString()}`,
                schedule_type: scheduleType,
                start_date: defaultDate,
                end_date: scheduleType === 'event' ? eventEndDate : null,
                ...(customBasePayload
                  ? { custom_base_grid: customBasePayload }
                  : {}),
              }),
          combinations,
          stores: storesToSend.map((s) => ({
            location_id: s.location_id,
            store_number: s.store_number,
            combination_index: s.combination_index,
            effective_date: s.effective_date || defaultDate || null,
          })),
        },
      )
      setCommitProgress({
        current: assignedStores.length,
        total: assignedStores.length,
      })
      onCommitted()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Commit failed')
    } finally {
      setCommitting(false)
      setCommitProgress(null)
    }
  }, [
    combinations,
    assignedStores,
    defaultDate,
    onCommitted,
    commitMode,
    appendToScheduleId,
    storeResolutions,
    baseMode,
    customBaseGrid,
  ])

  // --- Combination display helper ---
  const comboLabel = (combo: Combination) =>
    [...combo.delivery_days]
      .sort()
      .map((d) => DAY_LABELS[d === 0 ? 6 : d - 1])
      .join(' / ')

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ===== STEP 0: Upload & Parse ===== */}
      {activeStep === 0 && (
        <Box>
          {parsedStores.length === 0 ? (
            <>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? '#1976d2' : '#ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: isDragging ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.2s',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setIsDragging(false)
                  const file = e.dataTransfer.files?.[0]
                  if (file) processFile(file)
                }}
              >
                {uploading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      {uploadingStage || 'Processing…'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This may take ~10 seconds on the first run.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CloudUploadIcon
                      sx={{ fontSize: 40, color: '#999', mb: 1 }}
                    />
                    <Typography variant="body1">
                      Drag and drop a file here, or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supported formats: .xlsx, .csv
                    </Typography>
                  </>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Parsed: {filename}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {parsedStores.length} stores matched
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                  mb: 2,
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Store #
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Store Name
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Delivery Days
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 80,
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedStores.map((s) => (
                      <tr key={s.location_id}>
                        <td
                          style={{
                            padding: '4px 12px',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {s.store_number}
                        </td>
                        <td
                          style={{
                            padding: '4px 12px',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {s.location_name}
                        </td>
                        <td
                          style={{
                            padding: '4px 12px',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {s.delivery_days.join(', ')}
                        </td>
                        <td
                          style={{
                            padding: '4px 12px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                          }}
                        >
                          <Chip
                            label="Matched"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10 }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  disabled={parsedStores.length === 0}
                >
                  Confirm & Continue
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setParsedStores([])
                    setCombinations([])
                    setStoreAssignments([])
                    setFilename('')
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ===== STEP 1: Prep Base Schedule ===== */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The prep base schedule is the starting grid for the algorithm that
            builds each combination. Keep the Template Default, or customize it
            just for this schedule.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label="Use Template Default (Standard)"
              color={baseMode === 'template' ? 'primary' : 'default'}
              variant={baseMode === 'template' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setBaseMode('template')}
            />
            <Chip
              label="Customize for this schedule"
              color={baseMode === 'custom' ? 'primary' : 'default'}
              variant={baseMode === 'custom' ? 'filled' : 'outlined'}
              clickable
              onClick={() => setBaseMode('custom')}
            />
          </Box>

          {baseMode === 'custom' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Edits here only apply to this schedule. The Template is untouched
              and continues to be the starting point for other new schedules.
            </Alert>
          )}

          {templateGrid.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'auto',
                opacity: baseMode === 'template' ? 0.6 : 1,
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                  minWidth: 600,
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th
                      style={{
                        textAlign: 'left',
                        padding: '6px 12px',
                        borderBottom: '2px solid #e0e0e0',
                        minWidth: 160,
                      }}
                    >
                      Prep Item
                    </th>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                      (d) => (
                        <th
                          key={d}
                          style={{
                            textAlign: 'center',
                            padding: '6px 4px',
                            borderBottom: '2px solid #e0e0e0',
                            width: 55,
                          }}
                        >
                          {d}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(baseMode === 'custom' ? customBaseGrid : templateGrid).map(
                    (row, rowIdx) => (
                      <tr key={row.prep_item_id}>
                        <td
                          style={{
                            padding: '4px 12px',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {row.prep_item_name}
                        </td>
                        {(
                          [
                            'sunday',
                            'monday',
                            'tuesday',
                            'wednesday',
                            'thursday',
                            'friday',
                            'saturday',
                          ] as const
                        ).map((day) => (
                          <td
                            key={day}
                            style={{
                              padding: 0,
                              textAlign: 'center',
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            <Checkbox
                              checked={!!row[day]}
                              disabled={baseMode === 'template'}
                              onChange={() => {
                                setCustomBaseGrid((prev) =>
                                  prev.map((r, i) =>
                                    i === rowIdx ? { ...r, [day]: !r[day] } : r,
                                  ),
                                )
                              }}
                              size="small"
                            />
                          </td>
                        ))}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              disabled={templateGrid.length === 0}
              onClick={async () => {
                // why: if admin customized, re-run preview_combos with the
                // custom base so Step 2 shows grids derived from it.
                if (baseMode === 'custom' && customBaseGrid.length > 0) {
                  const patterns = combinations.map((c) => c.delivery_days)
                  const previewByKey = await fetchPreviewCombos(
                    patterns,
                    customBaseGrid,
                  )
                  setCombinations((prev) =>
                    prev.map((combo) => {
                      const preview = previewByKey.get(
                        [...combo.delivery_days]
                          .sort((a, b) => a - b)
                          .join(','),
                      )
                      return {
                        ...combo,
                        schedule_data: toFeGrid(preview?.schedule_data),
                        warnings: preview?.warnings || [],
                      }
                    }),
                  )
                }
                setActiveStep(2)
              }}
              sx={{ textTransform: 'none' }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}

      {/* ===== STEP 2: Delivery Combinations ===== */}
      {activeStep === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {combinations.length} delivery day combinations found. Configure
            safe items and prep schedule for each.
          </Typography>

          {editingComboIdx === null ? (
            <>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 8px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 50,
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 8px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 30,
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Delivery Days
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 80,
                        }}
                      >
                        Stores
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 80,
                        }}
                      >
                        Source
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 100,
                        }}
                      >
                        Safe Items
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 100,
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinations.map((combo, idx) => {
                      const isConfigured = combo.schedule_data.every((item) =>
                        DAY_KEYS.some((d) => item[d]),
                      )
                      return (
                        <tr
                          key={idx}
                          style={{
                            backgroundColor: isConfigured
                              ? '#f1f8e9'
                              : '#fff3e0',
                          }}
                        >
                          <td
                            style={{
                              padding: '6px 8px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            {isConfigured ? (
                              <CheckCircleIcon
                                sx={{ fontSize: 18, color: '#4caf50' }}
                              />
                            ) : (
                              <Chip
                                label="Setup"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            )}
                            {(() => {
                              const active = getActiveWarnings(combo, idx)
                              if (active.length === 0) return null
                              return (
                                <Chip
                                  label={`⚠ ${active.length}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  title={active
                                    .map((w) => {
                                      const itemName = allPrepItems.find(
                                        (p) => p.id === w.prep_item_id,
                                      )?.name
                                      return friendlyWarning(w, itemName).title
                                    })
                                    .join('\n')}
                                  sx={{ height: 20, fontSize: 10, ml: 0.5 }}
                                />
                              )
                            })()}
                          </td>
                          <td
                            style={{
                              padding: '6px 8px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                              color: '#999',
                            }}
                          >
                            {idx + 1}
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            {[...combo.delivery_days].sort().map((d) => (
                              <Chip
                                key={d}
                                icon={
                                  <LocalShippingIcon
                                    sx={{ fontSize: '14px !important' }}
                                  />
                                }
                                label={DAY_LABELS[d === 0 ? 6 : d - 1]}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mx: 0.25, height: 24, fontSize: 11 }}
                              />
                            ))}
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            {combo.store_count}
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            <Chip
                              label={
                                combo.source === 'file' ? 'File' : 'Manual'
                              }
                              size="small"
                              variant="outlined"
                              color={
                                combo.source === 'file' ? 'default' : 'warning'
                              }
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            {combo.delivery_items.length} items
                          </td>
                          <td
                            style={{
                              padding: '6px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setEditingComboIdx(idx)}
                              sx={{ textTransform: 'none', fontSize: 12 }}
                            >
                              Edit Grid
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Unassign stores from this combo
                                setStoreAssignments((prev) =>
                                  prev.map((s) =>
                                    s.combination_index === idx
                                      ? { ...s, combination_index: null }
                                      : s.combination_index !== null &&
                                        s.combination_index > idx
                                      ? {
                                          ...s,
                                          combination_index:
                                            s.combination_index - 1,
                                        }
                                      : s,
                                  ),
                                )
                                setCombinations((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }}
                              title="Delete combination"
                              sx={{ ml: 0.5 }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: 16, color: '#d32f2f' }}
                              />
                            </IconButton>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Box>
              {/* Add Combination */}
              <Box sx={{ mt: 2 }}>
                {!addingCombo ? (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setAddingCombo(true)
                      setNewComboDays(new Set())
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Combination
                  </Button>
                ) : (
                  <Box
                    sx={{
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Select delivery days:
                    </Typography>
                    {DAY_LABELS.map((label, i) => {
                      const dayNum = [1, 2, 3, 4, 5, 6, 0][i]
                      const isSelected = newComboDays.has(dayNum)
                      return (
                        <Chip
                          key={dayNum}
                          label={label}
                          size="small"
                          icon={
                            isSelected ? (
                              <LocalShippingIcon
                                sx={{ fontSize: '14px !important' }}
                              />
                            ) : undefined
                          }
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          clickable
                          onClick={() => {
                            setNewComboDays((prev) => {
                              const next = new Set(prev)
                              if (next.has(dayNum)) next.delete(dayNum)
                              else next.add(dayNum)
                              return next
                            })
                          }}
                          sx={{ fontSize: 12 }}
                        />
                      )
                    })}
                    <Button
                      size="small"
                      variant="contained"
                      disabled={newComboDays.size === 0}
                      onClick={async () => {
                        const days = Array.from(newComboDays).sort(
                          (a, b) => a - b,
                        )
                        // Pre-fill the new combo from the backend derivation too
                        const previewByKey = await fetchPreviewCombos([days])
                        const preview = previewByKey.get(days.join(','))
                        const newCombo: Combination = {
                          delivery_days: days,
                          delivery_items: Array.from(globalSafeItems),
                          schedule_data: toFeGrid(preview?.schedule_data),
                          warnings: preview?.warnings || [],
                          source: 'manual',
                          store_count: 0,
                        }
                        setCombinations((prev) => [...prev, newCombo])
                        setEditingComboIdx(combinations.length)
                        setAddingCombo(false)
                      }}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      Create
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setAddingCombo(false)}
                      sx={{ textTransform: 'none', fontSize: 12 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(3)}
                  // why: grids are auto-filled from the derivation rule, so the
                  // admin can proceed without configuring anything (review is
                  // optional). Only block if there are no combinations at all.
                  disabled={combinations.length === 0}
                >
                  Continue
                </Button>
                <Button variant="outlined" onClick={() => setActiveStep(1)}>
                  Back
                </Button>
              </Box>
            </>
          ) : (
            /* Editing a combination's grid */
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {comboLabel(combinations[editingComboIdx])} Schedule
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setEditingComboIdx(null)}
                  sx={{ textTransform: 'none' }}
                >
                  Back to list
                </Button>
              </Box>

              {/* Schedule warnings — live, update as the admin toggles cells */}
              {(() => {
                const active = getActiveWarnings(
                  combinations[editingComboIdx],
                  editingComboIdx,
                )
                if (active.length === 0) return null
                return (
                  <Alert severity="warning" sx={{ mb: 1.5, fontSize: 13 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Schedule warning{active.length > 1 ? 's' : ''} (
                      {active.length})
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {active.map((w, i) => {
                        const itemName = allPrepItems.find(
                          (p) => p.id === w.prep_item_id,
                        )?.name
                        const f = friendlyWarning(w, itemName)
                        return (
                          <Box component="li" key={i} sx={{ mb: 0.5 }}>
                            <Box sx={{ fontWeight: 500 }}>{f.title}</Box>
                            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                              {f.detail}
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  </Alert>
                )
              })()}

              {/* Safe items */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                >
                  Delivery Safe Items (stay ON during delivery days):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {allPrepItems.map((item) => {
                    const isSafe = combinations[
                      editingComboIdx
                    ].delivery_items.includes(item.id)
                    return (
                      <Chip
                        key={item.id}
                        label={item.name}
                        size="small"
                        color={isSafe ? 'info' : 'default'}
                        variant={isSafe ? 'filled' : 'outlined'}
                        clickable
                        onClick={() => toggleSafeItem(editingComboIdx, item.id)}
                        sx={{ fontSize: 11, height: 24 }}
                      />
                    )
                  })}
                </Box>
              </Box>

              {/* Prep grid */}
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'auto',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    minWidth: 600,
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          minWidth: 160,
                        }}
                      >
                        Prep Item
                      </th>
                      {DAY_KEYS.map((day, i) => {
                        const dayNum = DAY_KEY_TO_NUMBER[day]
                        const isDelivery =
                          combinations[editingComboIdx].delivery_days.includes(
                            dayNum,
                          )
                        return (
                          <th
                            key={day}
                            style={{
                              textAlign: 'center',
                              padding: '8px 6px',
                              borderBottom: '2px solid #e0e0e0',
                              width: 70,
                              backgroundColor: isDelivery
                                ? '#e3f2fd'
                                : '#f5f5f5',
                            }}
                          >
                            {DAY_LABELS[i]}
                            {isDelivery && (
                              <LocalShippingIcon
                                sx={{
                                  fontSize: 14,
                                  color: '#1976d2',
                                  ml: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            )}
                          </th>
                        )
                      })}
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '8px 6px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 50,
                        }}
                      >
                        Days
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinations[editingComboIdx].schedule_data.map((item) => {
                      const prepDays = DAY_KEYS.filter((d) => item[d]).length
                      const isZero = prepDays === 0
                      return (
                        <tr
                          key={item.prep_item_id}
                          style={{
                            backgroundColor: isZero ? '#ffebee' : 'transparent',
                          }}
                        >
                          <td
                            style={{
                              padding: '4px 12px',
                              borderBottom: '1px solid #eee',
                              fontWeight: isZero ? 600 : 400,
                              color: isZero ? '#d32f2f' : 'inherit',
                            }}
                          >
                            {item.prep_item_name}
                            {item.is_safe && (
                              <Chip
                                label="safe"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{
                                  ml: 0.5,
                                  height: 18,
                                  fontSize: 10,
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            )}
                          </td>
                          {DAY_KEYS.map((day) => {
                            const dayNum = DAY_KEY_TO_NUMBER[day]
                            const isDelivery =
                              combinations[
                                editingComboIdx
                              ].delivery_days.includes(dayNum)
                            return (
                              <td
                                key={day}
                                style={{
                                  textAlign: 'center',
                                  padding: '2px 6px',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: isDelivery
                                    ? isZero
                                      ? '#ffcdd2'
                                      : '#e3f2fd'
                                    : isZero
                                    ? '#ffebee'
                                    : 'transparent',
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={!!item[day]}
                                  disabled={!item.is_safe && isDelivery}
                                  onChange={() =>
                                    toggleGridDay(
                                      editingComboIdx,
                                      item.prep_item_id,
                                      day,
                                    )
                                  }
                                  sx={{
                                    p: 0.25,
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                  }}
                                />
                              </td>
                            )
                          })}
                          <td
                            style={{
                              textAlign: 'center',
                              padding: '4px 6px',
                              borderBottom: '1px solid #eee',
                              fontWeight: 600,
                              color: isZero ? '#d32f2f' : 'inherit',
                            }}
                          >
                            {prepDays}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ===== STEP 3: Store Assignment ===== */}
      {activeStep === 3 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {storeAssignments.length} stores. Verify or change each store's
            delivery combination.
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 500,
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 12px',
                      borderBottom: '2px solid #e0e0e0',
                    }}
                  >
                    Store #
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '6px 12px',
                      borderBottom: '2px solid #e0e0e0',
                    }}
                  >
                    Store Name
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '6px 12px',
                      borderBottom: '2px solid #e0e0e0',
                    }}
                  >
                    Combination
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '6px 12px',
                      borderBottom: '2px solid #e0e0e0',
                      width: 70,
                    }}
                  >
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {storeAssignments.map((store, sIdx) => (
                  <tr key={store.location_id}>
                    <td
                      style={{
                        padding: '4px 12px',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      {store.store_number}
                    </td>
                    <td
                      style={{
                        padding: '4px 12px',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      {store.location_name}
                    </td>
                    <td
                      style={{
                        padding: '4px 12px',
                        borderBottom: '1px solid #eee',
                        textAlign: 'center',
                      }}
                    >
                      <Autocomplete
                        size="small"
                        options={combinations.map((c, i) => ({
                          label: comboLabel(c),
                          value: i,
                        }))}
                        getOptionLabel={(o) => o.label}
                        value={
                          store.combination_index !== null
                            ? {
                                label: comboLabel(
                                  combinations[store.combination_index],
                                ),
                                value: store.combination_index,
                              }
                            : null
                        }
                        onChange={(_e, val) => {
                          setStoreAssignments((prev) =>
                            prev.map((s, i) =>
                              i === sIdx
                                ? {
                                    ...s,
                                    combination_index: val?.value ?? null,
                                  }
                                : s,
                            ),
                          )
                        }}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select..."
                            size="small"
                          />
                        )}
                        sx={{ minWidth: 180 }}
                      />
                    </td>
                    <td
                      style={{
                        padding: '4px 12px',
                        borderBottom: '1px solid #eee',
                        textAlign: 'center',
                      }}
                    >
                      <Chip
                        label={store.source === 'file' ? 'File' : 'Manual'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          {/* Add stores not in file */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Autocomplete
              size="small"
              options={allLocations.filter(
                (loc) =>
                  !storeAssignments.some((s) => s.location_id === loc.id),
              )}
              getOptionLabel={(o) => `${o.code} - ${o.name}`}
              onChange={(_e, val) => {
                if (!val) return
                setStoreAssignments((prev) => [
                  ...prev,
                  {
                    location_id: val.id,
                    store_number: val.code,
                    location_name: val.name,
                    combination_index: null,
                    effective_date: '',
                    source: 'manual',
                  },
                ])
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add a store..."
                  size="small"
                />
              )}
              sx={{ minWidth: 300 }}
              value={null}
            />
            <Typography variant="caption" color="text.secondary">
              Add stores not included in the uploaded file
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={() => setActiveStep(4)}>
              Continue
            </Button>
            <Button variant="outlined" onClick={() => setActiveStep(2)}>
              Back
            </Button>
          </Box>
        </Box>
      )}

      {/* ===== STEP 4: Schedule Info, Dates & Commit ===== */}
      {activeStep === 4 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
            Committing writes the grid as configured, not the one previewed in
            Delivery Combinations. To apply the four combination rules, open the
            schedule after committing and use Recompute Combinations. Leave it
            as configured if the arrangement is intentional.
          </Alert>
          {/* Create-new vs append-to-existing toggle */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: '#fafafa',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Create new schedule"
                color={commitMode === 'create' ? 'primary' : 'default'}
                variant={commitMode === 'create' ? 'filled' : 'outlined'}
                clickable
                onClick={() => {
                  setCommitMode('create')
                  setAppendToScheduleId(null)
                }}
              />
              <Chip
                label="Add to existing schedule"
                color={commitMode === 'append' ? 'primary' : 'default'}
                variant={commitMode === 'append' ? 'filled' : 'outlined'}
                clickable
                onClick={() => setCommitMode('append')}
                disabled={activeSchedules.length === 0}
              />
            </Box>
            {commitMode === 'append' && (
              <Box sx={{ mt: 1 }}>
                <Select
                  size="small"
                  displayEmpty
                  value={appendToScheduleId ?? ''}
                  onChange={(e) =>
                    setAppendToScheduleId(
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                  sx={{ minWidth: 360 }}
                >
                  <MenuItem value="" disabled>
                    Select a schedule…
                  </MenuItem>
                  {activeSchedules.map((s) => (
                    <MenuItem key={s.scheduleId} value={s.scheduleId}>
                      {s.scheduleName} — {s.scheduleType} ({s.storeCount}{' '}
                      stores, starts {s.startDate})
                    </MenuItem>
                  ))}
                </Select>
                {appendTarget && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Appending to <b>{appendTarget.scheduleName}</b> — name,
                    type, and dates inherit from the existing schedule.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Conflict table — only shown when appending and the upload has
              stores already on the target schedule */}
          {commitMode === 'append' &&
            appendToScheduleId !== null &&
            !loadingTarget &&
            storeConflicts.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {storeConflicts.length} store
                  {storeConflicts.length > 1 ? 's' : ''} already on this
                  schedule
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                  Pick which version to save per store. Default is "Use file"
                  (override).
                </Typography>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    fontSize: 12,
                    borderCollapse: 'collapse',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fff' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                        Store
                      </th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                        Existing days
                      </th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                        File days
                      </th>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                        Choose
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeConflicts.map((c) => {
                      const choice =
                        storeResolutions.get(c.location_id) || 'use_file'
                      return (
                        <tr key={c.location_id}>
                          <td style={{ padding: '4px 8px' }}>
                            {c.store_number} {c.location_name}
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            {c.existing_days.join(', ') || '—'}
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            {c.file_days.join(', ') || '—'}
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <Chip
                              label="Use file"
                              size="small"
                              color={
                                choice === 'use_file' ? 'primary' : 'default'
                              }
                              variant={
                                choice === 'use_file' ? 'filled' : 'outlined'
                              }
                              clickable
                              onClick={() =>
                                setStoreResolutions((prev) => {
                                  const next = new Map(prev)
                                  next.set(c.location_id, 'use_file')
                                  return next
                                })
                              }
                              sx={{ mr: 0.5, fontSize: 11 }}
                            />
                            <Chip
                              label="Keep existing"
                              size="small"
                              color={
                                choice === 'keep_existing'
                                  ? 'primary'
                                  : 'default'
                              }
                              variant={
                                choice === 'keep_existing'
                                  ? 'filled'
                                  : 'outlined'
                              }
                              clickable
                              onClick={() =>
                                setStoreResolutions((prev) => {
                                  const next = new Map(prev)
                                  next.set(c.location_id, 'keep_existing')
                                  return next
                                })
                              }
                              sx={{ fontSize: 11 }}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Box>
              </Alert>
            )}

          {/* Schedule info — hidden when appending (target's metadata is used) */}
          {commitMode === 'create' && (
            <>
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5 }}
                >
                  Schedule Info
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}
                >
                  <TextField
                    size="small"
                    label="Schedule Name"
                    value={scheduleName}
                    onChange={(e) => setScheduleName(e.target.value)}
                    placeholder="e.g., Spring 2026"
                    sx={{ minWidth: 250 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Regular"
                      color={scheduleType === 'regular' ? 'primary' : 'default'}
                      variant={
                        scheduleType === 'regular' ? 'filled' : 'outlined'
                      }
                      clickable
                      onClick={() => setScheduleType('regular')}
                      sx={{ fontSize: 12 }}
                    />
                    <Chip
                      label="Event"
                      color={scheduleType === 'event' ? 'warning' : 'default'}
                      variant={scheduleType === 'event' ? 'filled' : 'outlined'}
                      clickable
                      onClick={() => setScheduleType('event')}
                      sx={{ fontSize: 12 }}
                    />
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {scheduleType === 'regular'
                    ? 'Regular schedules are ongoing and replace the previous active schedule.'
                    : 'Event schedules are temporary and automatically end on the end date.'}
                </Typography>
              </Box>

              {/* Dates */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  label="Start Date"
                  value={defaultDate}
                  onChange={(e) => {
                    setDefaultDate(e.target.value)
                    setPastDateConfirmed(false)
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170 }}
                />
                {scheduleType === 'event' && (
                  <TextField
                    type="date"
                    size="small"
                    label="End Date"
                    value={eventEndDate}
                    onChange={(e) => setEventEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170 }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setStoreAssignments((prev) =>
                      prev.map((s) => ({ ...s, effective_date: defaultDate })),
                    )
                  }
                  sx={{ textTransform: 'none' }}
                >
                  Apply to all stores
                </Button>
              </Box>

              {/* Past date warning */}
              {defaultDate < new Date().toISOString().split('T')[0] &&
                !pastDateConfirmed && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      The start date is in the past. This schedule will activate
                      immediately and replace the current active schedule.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setPastDateConfirmed(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      I understand, proceed
                    </Button>
                  </Alert>
                )}

              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                  mb: 2,
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Store #
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Store Name
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                        }}
                      >
                        Combination
                      </th>
                      <th
                        style={{
                          textAlign: 'center',
                          padding: '6px 12px',
                          borderBottom: '2px solid #e0e0e0',
                          width: 160,
                        }}
                      >
                        Effective Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeAssignments
                      .filter((s) => s.combination_index !== null)
                      .map((store, sIdx) => (
                        <tr key={store.location_id}>
                          <td
                            style={{
                              padding: '4px 12px',
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            {store.store_number}
                          </td>
                          <td
                            style={{
                              padding: '4px 12px',
                              borderBottom: '1px solid #eee',
                            }}
                          >
                            {store.location_name}
                          </td>
                          <td
                            style={{
                              padding: '4px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            {store.combination_index !== null
                              ? comboLabel(
                                  combinations[store.combination_index],
                                )
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '4px 12px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            <TextField
                              type="date"
                              size="small"
                              value={store.effective_date || defaultDate}
                              onChange={(e) =>
                                setStoreAssignments((prev) =>
                                  prev.map((s, i) =>
                                    s.location_id === store.location_id
                                      ? { ...s, effective_date: e.target.value }
                                      : s,
                                  ),
                                )
                              }
                              InputProps={{ sx: { fontSize: 12, height: 30 } }}
                              sx={{ width: 145 }}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {commitProgress && (
              <Box sx={{ mb: 2, width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">Processing stores...</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {commitProgress.current} / {commitProgress.total}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(commitProgress.current / commitProgress.total) * 100}
                />
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleCommit}
              disabled={
                committing ||
                assignedStores.length === 0 ||
                (commitMode === 'append' && appendToScheduleId === null) ||
                (commitMode === 'create' &&
                  defaultDate < new Date().toISOString().split('T')[0] &&
                  !pastDateConfirmed) ||
                (commitMode === 'create' &&
                  scheduleType === 'event' &&
                  !eventEndDate)
              }
              startIcon={
                committing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
            >
              {committing
                ? `Committing (${commitProgress?.current ?? 0}/${
                    commitProgress?.total ?? 0
                  })...`
                : `Commit (${assignedStores.length} stores)`}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(3)}
              disabled={committing}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ImportWizard
